/**
 * normalizeDetailEvent — adapter for the `getEvent` single-event payload.
 *
 * The list endpoint (`listEvents`) returns flat events. The detail endpoint
 * (`getEvent`) returns a richer payload with nested `pilot`, `coPilot`,
 * `instructor`, `organization`, `eventResources` objects. This helper
 * extracts the legacy ID aliases (`pilotID`, `instructorID`, `resourceID`,
 * `locationID`) that `useCalendarPermissions.canEditEvent` and the rest of
 * the bridge code consume, on top of the existing flat `normalizeEvent`
 * pass.
 *
 * @module normalizeDetailEvent
 */
import { normalizeEvent } from './normalizeEvent.js';

const firstId = (...vals) => {
	for (const v of vals) {
		if (v == null) continue;
		if (typeof v === 'object') {
			if (v.id != null) return v.id;
			continue;
		}
		return v;
	}
	return null;
};

/**
 * @param {Object} ev Raw `getEvent` response (already unwrapped).
 * @returns {Object} Event with legacy ID aliases hoisted to top level.
 */
export function normalizeDetailEvent(ev) {
	if (!ev || typeof ev !== 'object') return ev;

	// Pull the first resource off `eventResources[]` (detail endpoint shape).
	// `resourceID` from the flat `normalizeEvent` is already correct for
	// list payloads; if both fields are present this prefers the existing one.
	const firstEventResource = Array.isArray(ev.eventResources) && ev.eventResources.length > 0
		? ev.eventResources[0]
		: null;
	const resourceFromNested = firstEventResource
		? firstId(
			firstEventResource.resourceID,
			firstEventResource.resourceId,
			firstEventResource.resource
		)
		: null;

	// Permissions read `pilotID` / `instructorID` / `locationID` / `resourceID`.
	// Detail endpoint nests these under `pilot.id` / `instructor.id` / etc.
	const pilotID = firstId(ev.pilotID, ev.pilotId, ev.pilot);
	const instructorID = firstId(ev.instructorID, ev.instructorId, ev.instructor);
	const secondInCommandID = firstId(
		ev.secondInCommandID,
		ev.secondInCommandId,
		ev.coPilotID,
		ev.coPilotId,
		ev.coPilot
	);
	const resourceID = firstId(ev.resourceID, ev.resourceId, resourceFromNested);
	const locationID = firstId(ev.locationID, ev.locationId, ev.location);
	const organizationUnique = ev.organizationUnique
		?? ev.organization?.unique
		?? ev.organization?.organizationUnique
		?? null;

	// Compose with the existing list-shape normalizer for camelCase aliasing.
	// We pass our hoisted IDs through so they win against any null defaults
	// inside `normalizeEvent`.
	const flat = normalizeEvent({
		...ev,
		pilotID,
		instructorID,
		resourceID,
		locationID
	});

	return {
		...flat,
		pilotID,
		instructorID,
		secondInCommandID,
		resourceID,
		locationID,
		organizationUnique,
		// Convenience: surface a flat `resourceIDs` alongside `eventResources`
		// so callers building edit form state can populate the multi-resource
		// chip list without re-walking the nested array.
		resourceIDs: Array.isArray(ev.eventResources)
			? ev.eventResources
				.map(er => firstId(er?.resourceID, er?.resourceId, er?.resource))
				.filter(id => id != null)
			: (ev.resourceIDs || ev.resourceIds || (resourceID ? [resourceID] : []))
	};
}
